/**
 * Glue layer between game state, Rive layers, Pixi chip animations, and the
 * minimal DOM status text. Mirrors the imperative side-effect orchestration
 * in `_GameScreenState` of
 *   packages/amgdragontiger_v2/lib/presentation/screens/game_screen.dart
 *
 * Input flow (all driven by Rive events emitted from the betting UI artboard):
 *   placeBetClick {betID}    → tryPlaceBet
 *   evtClickChips {chipIndex}→ selectChip
 *   evtClearClick            → clearAll
 *   evtButtonPlayClick       → confirm
 *
 * Output flow (driven by phase transitions + animation-end events):
 *   GamePhase.betting        → fireBetPanel(1) on popup, girlAnim(1), setBetLimits
 *   GamePhase.cardReveal     → fireBetPanel(2) on popup (NoMoreBet)
 *   Rive UIButtonClick noMoreBet_End → setCards + revealCard 1→2→3
 *   Rive UIButtonClick startBetEnd  → girlAnim(1)
 *   Rive evtResultStop        → girlAnim(2) + start result sequence
 *     t=2s: chip animation by winMode
 *     t=3s: showGameResult (BetPanel 3/4/5 on main+popup)
 *     t=6s: showWinLosePanel
 *   Rive evtYouWinEnd / evtYouLoseEnd → startBettingRound (new round)
 */
import type { BetType, GameResult, GameRound } from '../types/game';
import { BET_ITEM_ID, betTypeFromId, GAME_RESULT_HISTORY_VALUE, PAYOUT } from '../types/game';
import {
  DEFAULT_AGENT_ID,
  DEFAULT_GAME_CONFIG,
  DEFAULT_PASSWORD,
  GAME_KEY_MULTI,
  GAME_KEY_SINGLE,
} from '../config';
import type { Api } from '../net/api';
import { ApiError } from '../net/api';
import type { GameState, GameStateSnapshot } from './gameState';
import type { RiveLayer } from '../rive/riveLayer';
import type { ChipLayer } from '../pixi/chipLayer';
import type { Hud } from '../ui/hud';
import type { TableControls } from '../ui/controls';
import type { UrlParams } from './urlParams';
import type { MultiplayerHub, HubEvent } from '../net/multiplayerHub';
// showModeSelectionDialog disabled — mode now comes from the `gamemode`
// query param. Kept importable from '../ui/modeSelectionDialog' if re-enabled.
import { type GameMode } from '../ui/modeSelectionDialog';
import type { GameTimerWidget } from '../ui/timerWidget';
import { parseResultString } from './resultParser';
import { GameLogManager } from './gameLogManager';
import { debugLog, error, warn } from '../util/logger';

/**
 * Multiplayer only: end the visible bet countdown this many seconds before
 * the server's real betting-window close. The batched bet submission fires
 * when the countdown reaches 0, so this buffer gives it time to reach the
 * backend before betting closes and the round starts playing.
 */
const BET_SUBMIT_BUFFER_SEC = 2;

interface OrchestratorDeps {
  state: GameState;
  api: Api;
  rive: RiveLayer;
  chips: ChipLayer;
  hud: Hud;
  controls: TableControls;
  hub: MultiplayerHub;
  timer: GameTimerWidget;
  params: UrlParams;
  onInteractive?: () => void;
}

export class GameOrchestrator {
  private readonly d: OrchestratorDeps;
  private gid: string = '';

  // Result-sequence guard flags (mirror _didStartCardReveal /
  // _didStartResultSequence / _didResetAfterResult in game_screen.dart).
  // Reset every round.
  private didStartCardReveal = false;
  private didStartResultSequence = false;
  private didShowWinLose = false;
  // The post-card-reveal win/lose reveal point (the t=6s step in
  // startResultSequence) has been reached. Lets a late multiplayer settlement
  // Result drive the YOU WIN / YOU LOSE popup as a backstop (see handleResult):
  // by the time the Result arrives, the result sequence has usually already run
  // with a still-zero amount, so the captured `round` can't reveal it. Reset
  // every round, in BOTH round-start paths. Mirrors amg_carracing_pixi.
  private endingRevealDone = false;
  private didResetAfterResult = false;
  // Balance settlement is deferred to the WIN/LOSE reveal (t=6s) so the
  // displayed (available) balance stays "bet deducted" through the card
  // reveal and only reflects winnings once the result is shown.
  private pendingSettleBalance: number | null = null;
  private didSettle = false;
  // Multiplayer: the result reveal reached the settle point but the server's
  // Result event (which carries the post-round balance) hasn't arrived yet.
  // We hold the bets in place until it does (see applySettlement) so the
  // displayed available balance never flashes the pre-settlement wallet.
  private awaitingSettleBalance = false;
  // Monotonic round token, bumped at every round start (the SP startBettingRound
  // and the MP handleStartGame paths). Settlement now fetches the authoritative
  // balance from /action/balance (an async round-trip), so settleFromResult
  // captures the token up front and bails if a new round starts before the
  // balance arrives — a slow fetch must never apply a finished round's settlement
  // to the next round's wallet.
  private roundToken = 0;
  // Guards applyResultSettlement to once per round. A settlement Result can be
  // retransmitted by the server and slip past the index-dedup (which resets its
  // baseline at the inter-round restart), so without this a second settlement
  // would recompute netWinLose against the ALREADY-settled wallet (collapsing it
  // to ~0) and log a duplicate, corrupted round. didSettle only guards the
  // atomic settle, not the recompute/log/popup. Reset every round.
  private didApplyResultSettlement = false;
  // The history-road entry is appended once per round, at the result reveal
  // (NOT when the round data arrives), so the road never spoils the outcome
  // before the cards open. This guards that single append.
  private didRecordHistory = false;
  // Multiplayer bets are NOT sent per-chip; they're batched and submitted
  // when the bet countdown ends, so the player can still clear beforehand.
  private didSubmitMpBets = false;
  private sequenceTimers: number[] = [];

  // Multiplayer-specific timers.
  private betCountdownHandle: number | null = null;
  private restartCountdownHandle: number | null = null;
  private lastMpEventIndex: number | null = null;

  constructor(deps: OrchestratorDeps) {
    this.d = deps;
  }

  async start(): Promise<void> {
    this.d.state.on((next, prev) => this.onStateChange(next, prev));
    this.bindRiveEvents();
    this.bindHubEvents();
    this.d.controls.bindChips(this.d.state.config);
    this.d.controls.setSelectedChip(this.d.state.selectedChipIndex);
    this.d.controls.setControlsEnabled(false, false);

    this.d.hud.setUserName(this.d.params.userName);
    this.d.state.setPhase('initializing');
    this.d.hud.setStatus('Connecting…');

    try {
      await this.signIn();
      await this.loadConfigAndBalance();
      this.d.onInteractive?.();

      // Mode selection dialog disabled — the mode is now derived from the
      // launch URL the lobby routes to (single `/?g=DTA1` vs multi
      // `/dragontigerhub?g=DTAM`; see urlParams.resolveGameMode) instead of
      // prompting the player at launch. Original dialog code kept below
      // (commented) for reference.
      //
      // // Mode selection — mirrors game_screen.dart:671-713 BlocListener on
      // // GamePhase.modeSelection. Player picks single or multi player.
      // this.d.state.setPhase('modeSelection');
      // this.d.hud.setStatus('Select mode');
      // const mode: GameMode = await showModeSelectionDialog();
      // debugLog('[Orchestrator] mode selected:', mode);
      const mode: GameMode = this.d.params.gameMode;
      debugLog('[Orchestrator] mode from launch URL (g/path):', mode);

      if (mode === 'multi') {
        await this.startMultiplayer();
      } else {
        this.startBettingRound();
      }
    } catch (e) {
      error('[Orchestrator] start failed:', e);
      this.d.state.setError(extractMessage(e));
      this.d.hud.setStatus('ERROR: ' + extractMessage(e));
    }
  }

  // =================================================================
  // Bootstrap
  // =================================================================

  private async signIn(): Promise<void> {
    const { params } = this.d;
    const agentId = params.agentId || DEFAULT_AGENT_ID;

    let bearerToken: string;
    let gsid: string;

    if (params.token && params.gsid) {
      debugLog('[Orchestrator] Using token+gsid from URL params (no login)');
      bearerToken = ensureUrlEncoded(params.token);
      gsid = params.gsid;
    } else {
      this.d.hud.setStatus('Logging in…');
      debugLog('[Orchestrator] login', params.userName, agentId);
      const login = await this.d.api.login({
        userName: params.userName,
        agentId,
        password: DEFAULT_PASSWORD,
      });
      if (!login.sessionId) throw new Error('Login response missing SessionId');

      this.d.api.setCredentials({
        sessionToken: login.sessionId,
        gameSessionId: null,
        agentId: login.agentId || agentId,
        userName: params.userName,
      });

      this.d.hud.setStatus('Opening game…');
      const session = await this.d.api.openGame({
        userName: params.userName,
        agentId: login.agentId || agentId,
        gameKey: GAME_KEY_SINGLE,
      });
      debugLog('[Orchestrator] openGame OK, currentGameUrl=', session.currentGameUrl);

      const parsed = parseGameUrl(session.currentGameUrl, login.sessionId);
      bearerToken = ensureUrlEncoded(parsed.gameToken);
      gsid = parsed.gsid || session.sessionId;

      this.d.state.setBalance(session.balance);

      // openGame already returns Chips/MinBet/MaxBet — apply them so the
      // chip picker / bet limits are correct before betting begins.
      this.applyServerConfig(session.chips, session.minBet, session.maxBet);
    }

    this.d.api.setCredentials({
      sessionToken: bearerToken,
      gameSessionId: gsid,
      agentId,
      userName: params.userName,
    });
    this.gid = gsid;

    this.d.hud.setStatus('Fetching balance…');
    try {
      const balance = await this.d.api.checkBalance({ sessionId: gsid });
      this.d.state.setBalance(balance);
    } catch (e) {
      warn('[Orchestrator] checkBalance failed (continuing):', e);
    }
  }

  /**
   * Refresh balance + game config from /action/getinfo. Mirrors
   * `GameRepository.fetchGameConfig` (game_repository.dart:107-154):
   * extracts Chips/MinBet/MaxBet and applies them to the local state.
   * If the server doesn't return any of these fields, falls back to the
   * defaults declared in config.ts.
   */
  private async loadConfigAndBalance(): Promise<void> {
    const sessionId = this.d.api.getCredentials().gameSessionId ?? '';
    try {
      const info = await this.d.api.getInfo({ sessionId });
      if (info.balance > 0) this.d.state.setBalance(info.balance);
      this.applyServerConfig(info.chips, info.minBet, info.maxBet);
    } catch (e) {
      warn('[Orchestrator] getInfo failed (continuing with defaults):', e);
      // Ensure SOME config is set so the UI can render.
      if (this.d.state.config.chipValues.length === 0) {
        this.d.state.setConfig({
          minBet: DEFAULT_GAME_CONFIG.minBet,
          maxBet: DEFAULT_GAME_CONFIG.maxBet,
          chipValues: [...DEFAULT_GAME_CONFIG.chipValues],
        });
      }
    }
  }

  /**
   * Apply chip values + min/max bet from a server response, merging with the
   * defaults so a missing field doesn't wipe out the previously-applied one.
   */
  private applyServerConfig(
    chips: number[],
    minBet: number,
    maxBet: number,
  ): void {
    const current = this.d.state.config;
    const nextChips = chips.length > 0 ? chips : [...current.chipValues];
    const nextMin = minBet > 0 ? minBet : current.minBet;
    const nextMax = maxBet > 0 ? maxBet : current.maxBet;
    debugLog(
      '[Orchestrator] applyServerConfig chips=',
      nextChips,
      'min=',
      nextMin,
      'max=',
      nextMax,
    );
    this.d.state.setConfig({
      minBet: nextMin,
      maxBet: nextMax,
      chipValues: nextChips,
    });
  }

  // =================================================================
  // Rive event bindings (input + animation-end signals)
  // =================================================================

  private bindRiveEvents(): void {
    // ---- Input events fired by the betting Rive ----
    this.d.rive.on('placeBetClick', (payload) => {
      // payload may be { betID: 262 } or { betId } depending on the .riv build.
      const id = (payload.betID ?? payload.betId ?? payload.id) as number | undefined;
      if (typeof id !== 'number') return;
      const bet = betTypeFromId(id);
      if (bet) this.tryPlaceBet(bet);
    });

    this.d.rive.on('evtClickChips', (payload) => {
      const i = (payload.chipIndex ?? payload.index) as number | undefined;
      if (typeof i === 'number') this.d.state.selectChip(i);
    });

    this.d.rive.on('evtClearClick', () => this.clearAll());

    this.d.rive.on('evtButtonPlayClick', () => void this.confirm());

    // ---- Animation-end / sequence signals ----
    this.d.rive.on('UIButtonClick', (payload) => {
      const parts = String(payload.parts ?? '');
      if (parts === 'startBetEnd') {
        this.d.rive.triggerGirlAnim(1);
      } else if (parts === 'noMoreBet_End') {
        // Begin card-reveal sequence (mirrors game_screen.dart:531-547).
        if (this.didStartCardReveal) return;
        const round = this.d.state.currentRound;
        if (!round) return;
        this.didStartCardReveal = true;
        void this.playCardRevealSequence(round.dragonCard, round.tigerCard);
      }
    });

    this.d.rive.on('evtResultStop', () => {
      // Mirrors game_screen.dart:216-225: girl(2) + result sequence.
      this.d.rive.triggerGirlAnim(2);
      this.startResultSequence();
    });

    // After WIN/LOSE popup animation ends → new round.
    this.d.rive.on('evtYouWinEnd', () => this.resetToBettingIfNeeded());
    this.d.rive.on('evtYouLoseEnd', () => this.resetToBettingIfNeeded());
  }

  // =================================================================
  // Player actions
  // =================================================================

  /** Place a bet from the chip-on-area click. Validates against balance/max. */
  tryPlaceBet(bet: BetType): void {
    const s = this.d.state;
    if (s.phase !== 'betting') return;
    // In multiplayer, betting is gated by `canBet` (StartBet/EndBet plus our
    // countdown-end submit). game_bloc.dart:855-894.
    if (s.isMultiplayer && !s.multiplayerCanBet) return;

    const chip = s.selectedChipValue;
    const chipIndex = s.selectedChipIndex;
    const ok = s.placeBet(bet, chip);
    if (!ok) {
      debugLog('[Orchestrator] placeBet rejected', bet, chip);
      return;
    }
    // Visual chip flies in immediately and the available balance drops at
    // once (req: balance reflects the bet immediately). Bets are NOT sent to
    // the backend here — single-player submits on Play (confirm) and
    // multiplayer submits when the bet countdown ends — so they stay locally
    // clearable until then (req: player can still clear the bets).
    this.d.chips.placeChip(bet, chip, chipIndex);
  }

  /** Wipe all chips/bets locally. */
  clearAll(): void {
    const s = this.d.state;
    if (s.phase !== 'betting') return;
    // In multiplayer, once the countdown ends we auto-submit and lock the
    // bets — disallow clearing past that point.
    if (s.isMultiplayer && !s.multiplayerCanBet) return;
    s.clearBets();
    this.d.chips.hardClear();
  }

  /** Submit bets to the backend, then drive the card-reveal / result flow. */
  async confirm(): Promise<void> {
    if (this.d.state.phase !== 'betting') return;
    // In multiplayer the bets are batch-submitted automatically when the bet
    // countdown ends (see submitMultiplayerBets), so the Play button is a
    // no-op — the player just waits for the countdown to close betting.
    if (this.d.state.isMultiplayer) {
      debugLog('[Orchestrator] confirm() ignored in multiplayer mode');
      return;
    }

    const bets = this.d.state.bets.toBetLines();
    if (bets.length === 0) return;

    this.d.state.setPhase('bettingClosed');
    this.d.state.setPhase('waitingResult');

    try {
      const sid = this.d.api.getCredentials().gameSessionId ?? '';
      const result = await this.d.api.placeBet({
        sessionId: sid,
        bets,
        gid: this.gid || sid,
      });

      const parsed = parseResultString(result.result);
      this.gid = result.nextRoundId || this.gid;

      // Capture snapshot BEFORE state mutation for the log entry.
      const totalBet = this.d.state.bets.total;
      const betDetails = formatBetDetails(this.d.state.bets);
      const balanceBefore = this.d.state.balance;

      this.d.state.applyRoundResult({
        roundId: this.gid,
        nextRoundId: result.nextRoundId,
        dragonCard: parsed.dragonCardNumber,
        tigerCard: parsed.tigerCardNumber,
        dragonPoint: parsed.dragonPoint,
        tigerPoint: parsed.tigerPoint,
        result: parsed.gameResult,
        totalWinLose: result.totalWinLose,
        totalBet,
        positiveWinAmount: result.positiveWinAmount,
        hasPositiveWin: result.hasPositiveWin,
      });
      // Defer the balance update to the WIN/LOSE reveal (t=6s) so the
      // displayed balance stays "bet deducted" through the card reveal and
      // only reflects winnings when the result is shown (req 2).
      this.pendingSettleBalance = result.afterBalance;

      GameLogManager.add({
        betAmount: totalBet,
        betDetails,
        winLoseAmount: result.totalWinLose,
        balanceBefore,
        balanceAfter: result.afterBalance,
        gameResult: formatGameResult(
          parsed.gameResult,
          parsed.dragonPoint,
          parsed.tigerPoint,
        ),
        roundId: this.gid,
      });

      // Transition to cardReveal phase + fire NoMoreBet popup.
      // The actual card-reveal animation kicks off when Rive emits
      // UIButtonClick {parts: 'noMoreBet_End'}.
      this.d.state.setPhase('cardReveal');
      this.d.rive.fireBetPanel(2); // NoMoreBet popup
      this.d.hud.setStatus('No more bets');

      // Safety-net timer: if the Rive event never fires, force-start the
      // reveal after 8 s (matches the fallback in game_screen.dart:537-545).
      this.scheduleTimer(() => {
        if (this.didStartCardReveal) return;
        this.didStartCardReveal = true;
        const round = this.d.state.currentRound;
        if (round) void this.playCardRevealSequence(round.dragonCard, round.tigerCard);
      }, 8000);
    } catch (e) {
      error('[Orchestrator] placeBet failed:', e);
      const msg = extractMessage(e);
      if (e instanceof ApiError && e.unauthorized) {
        this.d.state.setError('Session expired. Please reload.');
      } else {
        this.d.state.setError(msg);
      }
      this.d.hud.setStatus('ERROR: ' + msg);
    }
  }

  /**
   * Submit the locally-accumulated bets to the multiplayer hub. Invoked when
   * the bet countdown ends (and, as a fallback, on the server's EndBet) —
   * the multiplayer analog of single-player confirm().
   *
   * One PlaceBet is sent per INDIVIDUAL chip (via toChipLines), not per
   * bet-type total: the hub validates each amount against the configured chip
   * denominations and rejects a summed total (e.g. 100+200 -> 300) with
   * "Invalid Chip Amount". The server adds each amount to the player's stake,
   * so the per-chip amounts sum to the same total. Idempotent: the
   * didSubmitMpBets guard prevents a double-submit when both the countdown
   * tick and EndBet fire.
   */
  private submitMultiplayerBets(): void {
    const s = this.d.state;
    if (!s.isMultiplayer) return;
    if (this.didSubmitMpBets) return;
    this.didSubmitMpBets = true;
    // Lock betting locally so no further chips/clears slip in after submit.
    s.setMultiplayerCanBet(false);

    const lines = s.bets.toChipLines();
    debugLog(
      '[Orchestrator] submitMultiplayerBets — submitting',
      lines.length,
      'chip line(s):',
      lines,
    );
    if (lines.length === 0) return;
    for (const line of lines) {
      void this.d.hub.placeBet(line.betItemId, line.amount);
    }
  }

  // =================================================================
  // Phase / animation orchestration
  // =================================================================

  /**
   * Begin a new betting round. Mirrors GamePhase.betting branch
   * (game_screen.dart:497-525):
   *   - clear sequence flags + timers
   *   - setBetLimits text
   *   - startBet popup + girlAnim(1)
   *   - render history icons
   */
  private startBettingRound(): void {
    this.cancelTimers();
    this.roundToken++;
    this.didStartCardReveal = false;
    this.didStartResultSequence = false;
    this.didShowWinLose = false;
    this.endingRevealDone = false;
    this.didResetAfterResult = false;
    this.pendingSettleBalance = null;
    this.didSettle = false;
    this.awaitingSettleBalance = false;
    this.didApplyResultSettlement = false;
    this.didRecordHistory = false;
    this.didSubmitMpBets = false;

    this.d.state.resetForNewRound();
    this.d.chips.hardClear();
    this.d.rive.clearAllHighlights();
    this.d.state.setPhase('betting');

    const cfg = this.d.state.config;
    this.d.rive.setBetLimits(`$ ${cfg.minBet}`, `$ ${cfg.maxBet}`);
    this.d.rive.setBalanceText(formatCurrency(this.d.state.availableBalance));
    this.d.rive.setBetAmountText('0');

    this.d.rive.fireBetPanel(1); // StartBet popup
    this.d.rive.triggerGirlAnim(1);
    this.d.rive.setAllHistory(this.d.state.history);

    this.d.hud.setStatus('Place your bets');
  }

  /**
   * Card reveal sequence. Mirrors rive_controller_manager.dart:688-705:
   *   setCards(d, t); revealCard(1); wait 1500ms; revealCard(2); wait 1000ms; revealCard(3);
   *
   * Values: 1=cards out, 2=Tiger open, 3=Dragon open.
   *
   * In Flutter, the result sequence is kicked off by the Rive `evtResultStop`
   * event after the reveal animation finishes (game_screen.dart:216-225).
   * If the .riv build we're using doesn't emit that event (different export
   * revision), we'd freeze after the reveal. Schedule a safety-net so the
   * result sequence always starts a moment after the last reveal step.
   */
  private async playCardRevealSequence(d: number, t: number): Promise<void> {
    this.d.rive.setCards(d, t);
    this.d.rive.setCardRevealStep(1);
    await wait(1500);
    this.d.rive.setCardRevealStep(2);
    await wait(1000);
    this.d.rive.setCardRevealStep(3);

    // Safety net: trigger the result sequence ~1.5s after the final card
    // is shown if `evtResultStop` never arrives.
    this.scheduleTimer(() => {
      if (this.didStartResultSequence) return;
      debugLog('[Orchestrator] Rive evtResultStop never fired — forcing result sequence');
      this.d.rive.triggerGirlAnim(2);
      this.startResultSequence();
    }, 1500);
  }

  /**
   * Result sequence. Mirrors game_screen.dart `_startResultSequence`
   * (~lines 260-340):
   *   t=2s : animate chips by winMode (0=halfHalf, 1=dealer, 2=player) + gather sound
   *   t=3s : showGameResult (BetPanel 3/4/5 on main+popup+card)
   *   t=6s : show win/lose panel (skipped on no-bet/push)
   */
  private startResultSequence(): void {
    if (this.didStartResultSequence) return;
    // Bail BEFORE latching the guard: if the round data isn't ready yet (no
    // StartPlay, a reordered/replayed event), latching here would permanently
    // wedge the sequence — the t=6s applySettlement timer would never be
    // scheduled, so the deferred balance settle would never fire and the round's
    // win/loss would silently never apply. Leaving the guard unset lets a later
    // trigger (evtResultStop, the card-reveal safety net, or the next Result)
    // start it once currentRound exists.
    const round = this.d.state.currentRound;
    if (!round) return;
    this.didStartResultSequence = true;
    this.cancelTimers();

    // t=2s — chips animation
    this.scheduleTimer(() => {
      const winLose = round.totalWinLose;
      if (winLose > 0) {
        const winningBets = this.placedWinningBets(round.result);
        this.d.chips.animateWinningChips(winningBets);
      } else if (winLose < 0) {
        this.d.chips.animateLosingChips();
      } else {
        this.d.chips.animateTie();
      }
    }, 2000);

    // t=3s — show game result on main + popup BetPanel. The cards are open by
    // now, so this is where the outcome becomes visible — record the history
    // road entry here (not at StartPlay) so the road never spoils the result.
    this.scheduleTimer(() => {
      this.d.rive.clearAllHighlights();
      this.d.rive.setHighlight(round.result, true);
      this.d.rive.fireBetPanel(this.panelCodeFor(round.result));
      this.recordHistoryOnce(round.result);
      this.d.state.setPhase('showResult');
    }, 3000);

    // t=6s — settle the balance, then show the WIN/LOSE popup.
    this.scheduleTimer(() => {
      // Reflect the post-round balance now that the result is shown (req 2).
      // Clears the resolved bets in the same commit so the available balance
      // lands exactly on the server's settled value.
      this.applySettlement();
      // Mark the reveal point reached so a late multiplayer settlement Result
      // can drive the popup as a backstop (see handleResult).
      this.endingRevealDone = true;
      // Read the figure FRESH from currentRound rather than the captured
      // `round`. In multiplayer the settlement Result patches the real win/lose
      // into currentRound (via applyRoundResult, which REPLACES it) and can land
      // in the gap before this timer fires — the captured `round` would still
      // hold the StartPlay placeholder (winLose 0 → no popup, the multiplayer
      // bug). Fall back to the captured round only if currentRound was already
      // nulled. Mirrors amg_carracing_pixi's onSpinEnd reveal.
      const display = this.displayWinLoseFor(this.d.state.currentRound ?? round);
      this.revealWinLosePanel(display);
      if (display !== 0) {
        this.d.state.setPhase('result');
      } else if (!this.d.state.isMultiplayer) {
        // Single-player push / no-bet — no popup; straight back to betting.
        this.resetToBettingIfNeeded();
      }
      // Multiplayer with display still 0: the settlement Result hasn't arrived
      // yet. The backstop in handleResult reveals the popup the instant it does;
      // the t=11s safety net (and evtYouWinEnd/evtYouLoseEnd) handle teardown.
    }, 6000);

    // Safety net — if Rive evtYouWinEnd / evtYouLoseEnd never fires,
    // force the post-result teardown (revealCard 4 → 5 → new round)
    // at t=11 s. Matches Flutter fallback (game_screen.dart:336-340).
    this.scheduleTimer(() => this.resetToBettingIfNeeded(), 11000);
  }

  /**
   * The win/lose figure to DISPLAY for a round, per the product rule: if the
   * player won ANY bet area, show the WINNINGS from the winning area(s) EXCLUDING
   * the staked amount ("modal") — even when the round nets a loss overall (still
   * "YOU WIN"); otherwise show the net loss; otherwise 0 (push / no bet).
   * `positiveWinAmount` carries those stake-free winnings (single-player from the
   * REST bet rows; multiplayer computed from the won area in handleResult);
   * `totalWinLose` is the net. Positive = win, negative = loss, 0 = neither.
   *
   * Example: bet tie 100 + tiger 100, result tiger → won the tiger area →
   * positiveWinAmount = 100 → YOU WIN 100 (NOT a net-0 push / loss).
   */
  private displayWinLoseFor(round: GameRound | null): number {
    if (!round) return 0;
    if (round.positiveWinAmount > 0) return round.positiveWinAmount;
    return round.totalWinLose < 0 ? round.totalWinLose : 0;
  }

  /**
   * Show the YOU WIN / YOU LOSE popup exactly once per round. `display` follows
   * displayWinLoseFor: positive = win (gross winnings — fires whenever the player
   * won ANY area, even on a net loss), negative = lose (net loss), 0 = nothing
   * (push / no bet → no popup, and the guard is left UNSET so a later caller with
   * the real figure can still reveal it). Idempotent via didShowWinLose.
   *
   * The caller passes `display` EXPLICITLY rather than this reading currentRound
   * itself, because in multiplayer the authoritative settlement Result can land
   * after the t=6s reveal already ran (with a still-zero amount) — the popup must
   * be driven from the figure extracted in handleResult, not the by-then-stale
   * captured round. Mirrors amg_carracing_pixi's revealWinLosePanel.
   */
  private revealWinLosePanel(display: number): void {
    if (this.didShowWinLose) return;
    if (display === 0) return;
    this.didShowWinLose = true;
    this.d.rive.showWinLose(display > 0, formatCurrency(Math.abs(display)));
    this.d.hud.setStatus(
      display > 0
        ? `YOU WIN ${formatCurrency(display)}`
        : `YOU LOSE ${formatCurrency(-display)}`,
    );
  }

  /**
   * Tear-down sequence between rounds. Mirrors
   *   game_screen.dart:343-368 `_resetToBettingIfNeeded`:
   *
   *     revealCard(4)              // 4 = close   — fired immediately
   *     +500 ms  revealCard(5)     // 5 = keep away
   *     +1000 ms total: clear chips, refresh history icons, StartNewRound
   *
   * Idempotent — guarded by `didResetAfterResult` like Flutter's
   * `_didResetAfterResult` flag.
   */
  /**
   * Apply the deferred balance settlement: set the wallet to the server's
   * post-round value and clear the resolved bets (atomically, to avoid a
   * one-frame flash of an intermediate available balance). Idempotent — safe
   * to call from the t=6s timer and again from the teardown safety nets. If
   * the server balance isn't known yet (a late multiplayer Result), just
   * clears the bets; handleResult applies the balance the moment it arrives.
   */
  private applySettlement(): void {
    if (this.didSettle) return;
    if (this.pendingSettleBalance != null) {
      // Server's post-round balance is known — finalize atomically.
      this.didSettle = true;
      this.d.state.settle(this.pendingSettleBalance);
    } else {
      // Multiplayer: the reveal reached the settle point but the server's
      // Result event (carrying the post-round balance) hasn't arrived yet.
      // Do NOT clear the bets here — the displayed balance is
      // `wallet − pending bets`, so clearing the bets while the wallet still
      // holds the pre-settlement value would make the balance jump UP to the
      // full (pre-bet) wallet for a moment (the "balance flashes to the old
      // value" bug). Instead, leave the bets so the display stays
      // bet-deducted, and let handleResult finalize atomically the instant the
      // balance arrives.
      this.awaitingSettleBalance = true;
    }
  }

  /**
   * Append this round's result to the history road exactly once. Primary call
   * is at the t=3s reveal (when the cards are open and the winner is shown);
   * resetToBettingIfNeeded calls it again as a safety net in case that reveal
   * step was skipped, so a round can never be dropped from the road.
   */
  private recordHistoryOnce(result: GameResult): void {
    if (this.didRecordHistory) return;
    this.didRecordHistory = true;
    this.d.state.recordRoundHistory(result);
  }

  private resetToBettingIfNeeded(): void {
    if (this.didResetAfterResult) return;
    const p = this.d.state.phase;
    if (p !== 'result' && p !== 'showResult' && p !== 'cardReveal') return;
    this.didResetAfterResult = true;

    // Safety net: ensure the balance was settled even if the t=6s timer was
    // skipped (e.g. a Rive evtYouWinEnd fired early).
    this.applySettlement();

    // Safety net: ensure this round made it onto the history road even if the
    // t=3s reveal step was skipped (e.g. an early teardown cancelled it).
    const finished = this.d.state.currentRound;
    if (finished) this.recordHistoryOnce(finished.result);

    this.cancelTimers();

    // Step 4 — close the cards immediately.
    this.d.rive.setCardRevealStep(4);

    // Step 5 — "keep away" 500 ms later.
    this.scheduleTimer(() => this.d.rive.setCardRevealStep(5), 500);

    // 500 ms after that: history update + chip clear + new round.
    // In multiplayer, do NOT call startBettingRound() — the hub's StartGame
    // (after the restart countdown) drives the round reset, and StartBet
    // fires the popup. Calling it locally would show the StartBet popup
    // during the 5s restart window.
    this.scheduleTimer(() => {
      this.d.rive.setAllHistory(this.d.state.history);
      this.d.chips.hardClear();
      if (!this.d.state.isMultiplayer) this.startBettingRound();
    }, 1000);
  }

  // =================================================================
  // State-change → output sync
  // =================================================================

  private onStateChange(next: GameStateSnapshot, prev: GameStateSnapshot): void {
    if (next.balance !== prev.balance) {
      this.renderBalance();
    }

    if (next.bets !== prev.bets) {
      this.d.hud.setBetTotal(next.bets.total);
      this.d.rive.setBetAmountText(formatCurrency(next.bets.total));
      // Placing or clearing a bet changes the available balance — reflect
      // it immediately (req: balance updates the instant a bet is placed).
      this.renderBalance();
      this.d.controls.setControlsEnabled(this.canInteract(next), !next.bets.isEmpty);
    }

    if (next.selectedChipIndex !== prev.selectedChipIndex) {
      this.d.controls.setSelectedChip(next.selectedChipIndex);
    }

    if (
      next.phase !== prev.phase ||
      next.multiplayerCanBet !== prev.multiplayerCanBet
    ) {
      this.d.controls.setControlsEnabled(this.canInteract(next), !next.bets.isEmpty);
    }

    if (next.config !== prev.config) {
      this.d.controls.bindChips(next.config);
      this.d.controls.setSelectedChip(next.selectedChipIndex);
    }

    if (next.history !== prev.history) {
      this.d.rive.setAllHistory(next.history);
    }

    // Drive the timer widget. Bet countdown wins over restart countdown
    // — mirrors game_screen.dart:797-862 (conditional render with ValueKey).
    if (
      next.multiplayerBetSeconds !== prev.multiplayerBetSeconds ||
      next.multiplayerRestartSeconds !== prev.multiplayerRestartSeconds
    ) {
      if (next.multiplayerBetSeconds != null) {
        this.d.timer.showBetTimer(next.multiplayerBetSeconds);
      } else if (next.multiplayerRestartSeconds != null) {
        this.d.timer.showRestartTimer(next.multiplayerRestartSeconds);
      } else {
        this.d.timer.showBetTimer(null);
      }
    }
  }

  /** Push the available balance (wallet − pending bets) to HUD + Rive. */
  private renderBalance(): void {
    const avail = this.d.state.availableBalance;
    this.d.hud.setBalance(avail);
    this.d.rive.setBalanceText(formatCurrency(avail));
  }

  /**
   * Whether chip / play / clear controls should be live. Single-player: the
   * betting phase. Multiplayer: the hub's StartBet/EndBet and our
   * countdown-end submit gate it via `multiplayerCanBet`.
   */
  private canInteract(s: GameStateSnapshot): boolean {
    return s.isMultiplayer ? s.multiplayerCanBet : s.phase === 'betting';
  }

  /**
   * Normalise a hub-event payload into a single envelope object.
   *
   * The server uses a few shapes interchangeably:
   *   - { Index, Data, Content, State, ... }                     — direct
   *   - [ envelope ]                                              — single-elem array
   *   - [ 'Success'|'Fail', envelope ]                            — status + envelope
   *   - JSON string (already decoded upstream in normalizePayload)
   *
   * We pick the first object element so the rest of the handler code can
   * read `env.Data` / `env.Content` etc. uniformly.
   */
  private normalizeEnvelope(payload: unknown): Envelope {
    if (payload == null) return null;
    if (Array.isArray(payload)) {
      // Find the first object-shaped element. This handles both ['env']
      // and ['Fail'|'Success', env] shapes.
      for (const item of payload) {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          // Attach the leading status string as `_status` if it's there,
          // so handlers can detect 'Fail'.
          const status =
            typeof payload[0] === 'string' ? (payload[0] as string) : null;
          if (status) {
            return { _status: status, ...(item as Record<string, unknown>) };
          }
          return item as Record<string, unknown>;
        }
      }
      return null;
    }
    if (typeof payload === 'object') return payload as Envelope;
    return null;
  }

  // =================================================================
  // Multiplayer — hub bootstrap + event router
  // =================================================================

  /**
   * Enter multiplayer mode. Mirrors `_onStartMultiplayer` (game_bloc.dart:376-446):
   *   1. Open the SignalR connection.
   *   2. JoinGame(sessionId=gsid, gameKey='DTAM').
   *   3. GetMessages() so the server replays the current round state.
   *
   * The connection is also told to use the multiplayer game key going
   * forward — this only affects what we send over the wire, not the
   * REST credentials.
   */
  private async startMultiplayer(): Promise<void> {
    this.d.state.setMultiplayerActive(true);
    this.d.state.setPhase('initializing');
    this.d.hud.setStatus('Connecting hub…');

    try {
      await this.d.hub.connect();

      // JoinGame's sessionId is the URL-DECODED bearer token, NOT the gsid.
      // Mirrors game_screen.dart:153-167:
      //   final sessionId = token.contains('%') ? Uri.decodeComponent(token) : token;
      // We store the encoded form for REST Bearer headers, so decode it here.
      const encodedToken = this.d.api.getCredentials().sessionToken ?? '';
      const sessionId = encodedToken.includes('%')
        ? decodeURIComponent(encodedToken)
        : encodedToken;
      if (!sessionId) throw new Error('Missing session token for JoinGame');

      debugLog(
        '[Orchestrator] joining hub with sessionId (decoded token), gameKey=DTAM',
      );
      await this.d.hub.joinGame(sessionId, GAME_KEY_MULTI);
      await this.d.hub.getMessages();
      debugLog('[Orchestrator] joined multiplayer table');
      this.d.hud.setStatus('Waiting for next round…');
    } catch (e) {
      error('[Orchestrator] multiplayer connect failed:', e);
      this.d.state.setError(extractMessage(e));
      this.d.state.setMultiplayerActive(false);
    }
  }

  /**
   * Subscribe to hub events. Single listener — dispatches by event name.
   * Mirrors the giant switch in game_bloc.dart `_onMultiplayerHubEventReceived`
   * (lines 479-710).
   */
  private bindHubEvents(): void {
    this.d.hub.on((event) => this.handleHubEvent(event));
  }

  private handleHubEvent(event: HubEvent): void {
    const env = this.normalizeEnvelope(event.payload);

    // Index-based dedup (game_bloc.dart:518-535). The server retransmits
    // some events; we ignore any envelope with a smaller-or-equal Index
    // than the last one we processed.
    if (env && typeof env.Index === 'number') {
      const idx = env.Index;
      if (this.lastMpEventIndex != null && idx <= this.lastMpEventIndex) return;
      this.lastMpEventIndex = idx;
    }

    // The server's Index counter RESETS to 1 every round. When we see the
    // inter-round restart we have to clear `lastMpEventIndex` so the next
    // round's StartGame {Index:1} / StartBet {Index:2} aren't dropped by the
    // check above — otherwise handleStartGame never runs, the per-round guards
    // (endingRevealDone / didShowWinLose / …) never reset, and the win/lose
    // popup (plus betting controls) stay dead from round 2 onward. The restart
    // is signalled either by State='restart' (game_bloc.dart:544-547) OR by a
    // Result carrying a restart countdown (StartTime/EndTime) — match BOTH, so
    // this stays consistent with handleResult's own restart detection and with
    // the carracing reference port.
    const isRestart =
      String(env?.State ?? '').toLowerCase() === 'restart' ||
      (event.name === 'Result' &&
        (env?.StartTime != null || env?.EndTime != null));
    if (env && isRestart) {
      this.lastMpEventIndex = null;
    }

    switch (event.name) {
      case '_connection':
        // event.payload is { state, connectionId?, error? }.
        debugLog('[Hub] connection state', event.payload);
        break;

      case 'PlayerJoined':
        this.handlePlayerJoined(env);
        break;

      case 'PlayerLeft':
        this.handlePlayerLeft(env);
        break;

      case 'StartGame':
        this.handleStartGame(env);
        break;

      case 'StartBet':
        this.handleStartBet(env);
        break;

      case 'EndBet':
        this.handleEndBet(env);
        break;

      case 'StartPlay':
        this.handleStartPlay(env);
        break;

      case 'Result':
        this.handleResult(env);
        break;

      case 'PlaceBet':
        this.handlePlaceBetAck(env);
        break;

      case 'GetMessages':
        // Server replays a backlog of previous events — they each fire
        // their own server-event listener too, so nothing extra to do.
        break;

      default:
        debugLog('[Hub] unhandled event', event.name, env);
    }
  }

  // -----------------------------------------------------------------
  // Individual hub handlers — kept short, just wire to state / animations.
  // -----------------------------------------------------------------

  private handlePlayerJoined(env: Envelope): void {
    // Surface server-side join failures (e.g. invalid session token).
    if (env?._status === 'Fail') {
      const msg = String(env.Content ?? 'Failed to join multiplayer table');
      error('[Hub] PlayerJoined Fail:', msg);
      this.d.state.setError(msg);
      this.d.hud.setStatus('ERROR: ' + msg);
      this.stopMultiplayer();
      this.d.state.setPhase('modeSelection');
      return;
    }

    // Server may return Chips/MinBet/MaxBet on join — apply them. Mirrors
    // game_bloc.dart:594-613 (preserves any prior min/max).
    const game = (env?.Data && typeof env.Data === 'object'
      ? (env.Data as { Game?: unknown }).Game
      : undefined) as Record<string, unknown> | undefined;
    if (game) {
      const chips = parseChipsField(game.Chips ?? game.chips);
      if (chips.length > 0) {
        this.d.state.setConfig({
          ...this.d.state.config,
          chipValues: chips,
        });
      }
    }
  }

  private handlePlayerLeft(env: Envelope): void {
    const msg = String(env?.Content ?? 'You have been disconnected.');
    debugLog('[Hub] PlayerLeft kick:', msg);
    this.d.state.setPlayerKickedMessage(msg);
    this.stopMultiplayer();
    this.d.state.setPhase('modeSelection');
    this.d.hud.setStatus('Session ended — please reload');
  }

  /**
   * StartGame — round reset announcement. Mirrors game_bloc.dart:632-650.
   *
   * Cards are NOT closed here. The closing animation lives in
   * `handleStartPlay` (just before the new cards are revealed) so it
   * lines up with the Rive deal-cards animation rather than with the
   * restart countdown.
   */
  private handleStartGame(_env: Envelope): void {
    this.cancelBetCountdown();
    this.cancelRestartCountdown();
    this.cancelTimers();

    this.roundToken++;
    this.didStartCardReveal = false;
    this.didStartResultSequence = false;
    this.didShowWinLose = false;
    this.endingRevealDone = false;
    this.didResetAfterResult = false;
    this.pendingSettleBalance = null;
    this.didSettle = false;
    this.awaitingSettleBalance = false;
    this.didApplyResultSettlement = false;
    this.didRecordHistory = false;
    this.didSubmitMpBets = false;

    this.d.state.setMultiplayerCanBet(false);
    this.d.state.setMultiplayerBetSeconds(null);
    this.d.state.setMultiplayerRestartSeconds(null);
    this.d.state.resetForNewRound();
    this.d.chips.hardClear();
    this.d.rive.clearAllHighlights();
    this.d.state.setPhase('newRound');
    this.d.rive.setAllHistory(this.d.state.history);
  }

  /**
   * StartBet — open the betting window. Mirrors game_bloc.dart:652-677.
   *
   * NOTE: StartBet popup / NoMoreBet popup / card-close timing is
   * intentionally kept in the most basic shape here. Tune from here.
   */
  private handleStartBet(env: Envelope): void {
    const seconds = secondsBetween(env?.StartTime, env?.EndTime);
    debugLog('[Hub] StartBet — countdown', seconds, 's');
    // Fresh bet window — allow a new batched submission this round.
    this.didSubmitMpBets = false;
    this.d.state.setPhase('betting');
    this.d.state.setMultiplayerCanBet(true);
    // Apply server-configured min/max bet text. In single player this happens
    // in startBettingRound(), but multiplayer never calls that (the hub drives
    // rounds), so without this the MinMax nested artboard stays blank.
    // Mirrors game_screen.dart:505 — setBetLimits runs on every GamePhase.betting
    // entry, for both modes, off the same getInfo-loaded config.
    const cfg = this.d.state.config;
    this.d.rive.setBetLimits(`$ ${cfg.minBet}`, `$ ${cfg.maxBet}`);
    this.d.rive.fireBetPanel(1); // StartBet popup
    this.d.rive.triggerGirlAnim(1);
    this.d.hud.setStatus('Place your bets');
    // End the visible countdown BET_SUBMIT_BUFFER_SEC seconds early so the
    // batched submit (at countdown 0) reaches the backend before the real
    // window closes and play starts.
    this.startBetCountdown(Math.max(0, seconds - BET_SUBMIT_BUFFER_SEC));
  }

  /** EndBet — close the betting window. Mirrors game_bloc.dart:679-690. */
  private handleEndBet(_env: Envelope): void {
    this.cancelBetCountdown();
    // Submit now if the local countdown didn't already (server closed the
    // window first). Idempotent via didSubmitMpBets.
    this.submitMultiplayerBets();
    this.d.state.setMultiplayerCanBet(false);
    this.d.state.setMultiplayerBetSeconds(null);
    this.d.state.setPhase('bettingClosed');
    this.d.rive.fireBetPanel(2); // NoMoreBet popup
    this.d.hud.setStatus('No more bets');
  }

  /**
   * StartPlay — the round's cards are revealed. Mirrors
   * `_handleMultiplayerStartPlay` (game_bloc.dart:712-757). The Result
   * field is the same CSV format as single-player: parseResultString.
   *
   * BEFORE revealing the new cards, run the inter-round card-close
   * animation (revealCard 4 then 5) so any face-up cards from the
   * previous round are tucked away first. This is the right moment
   * because:
   *   - StartGame (which clears chips) fires too early (during the
   *     restart countdown).
   *   - Rive's evtYouWinEnd is unreliable timing-wise (and we don't
   *     want it to drive a `startBettingRound` in multiplayer at all).
   */
  private handleStartPlay(env: Envelope): void {
    const raw = String(env?.Result ?? '');
    if (!raw) return;
    let parsed;
    try {
      parsed = parseResultString(raw);
    } catch (e) {
      warn('[Hub] StartPlay parse failed:', e, raw);
      return;
    }
    this.d.state.setMultiplayerResultString(raw);
    this.d.state.applyRoundResult({
      roundId: '',
      nextRoundId: '',
      dragonCard: parsed.dragonCardNumber,
      tigerCard: parsed.tigerCardNumber,
      dragonPoint: parsed.dragonPoint,
      tigerPoint: parsed.tigerPoint,
      result: parsed.gameResult,
      // WinLose isn't known yet — comes via the Result event.
      totalWinLose: 0,
      totalBet: this.d.state.bets.total,
      positiveWinAmount: 0,
      hasPositiveWin: false,
    });

    if (!this.didStartCardReveal) {
      this.didStartCardReveal = true;
      this.d.state.setPhase('cardReveal');
      void this.playCardRevealSequence(
        parsed.dragonCardNumber,
        parsed.tigerCardNumber,
      );
    }
  }

  /**
   * Result — settlement. Mirrors `_handleMultiplayerResult` (game_bloc.dart:759-853).
   *
   * Two shapes:
   *   - State == 'restart' (or similar) → starts the restart countdown.
   *   - Otherwise → Data array contains per-user settlement objects with
   *     UserName + WinLost + Balance fields. Find the entry for the
   *     current user and apply.
   */
  private handleResult(env: Envelope): void {
    const state = String(env?.State ?? '').toLowerCase();

    if (state === 'restart' || env?.StartTime || env?.EndTime) {
      const seconds = secondsBetween(env?.StartTime, env?.EndTime);
      if (seconds > 0) {
        debugLog('[Hub] Result/restart — restart countdown', seconds, 's');
        this.d.state.setMultiplayerGameState('restart');
        this.startRestartCountdown(seconds);
        return;
      }
    }

    // Start the visual result sequence right away — it's purely cosmetic (chips,
    // game-result popup) and must NOT wait on the balance round-trip below.
    // Idempotent: Rive's evtResultStop (or the forced fallback) may have already
    // started it.
    if (!this.didStartResultSequence) this.startResultSequence();

    // Settle off the AUTHORITATIVE post-round balance. The backend stopped
    // returning a usable per-bet figure (WinLost is 0 even on a loss) and the
    // Result event no longer carries a per-user Balance, so the wallet can't be
    // derived locally — we read it from the server (/action/balance). Async.
    void this.settleFromResult(env);
  }

  /**
   * Resolve the player's authoritative post-round balance, then run settlement.
   *
   * Balance source, in order of preference:
   *   1. The Result event's per-user `Balance` field — used when the server
   *      still sends it.
   *   2. /action/balance (checkBalance) — the backend now omits Balance from the
   *      Result event AND returns WinLost=0 even on a loss, so neither the wallet
   *      nor the net P&L can be derived locally; the only correct source is the
   *      server's own balance. NOTE: /action/getinfo does NOT carry a balance
   *      (it returns config only — Chips/MinBet/MaxBet/GameOdds), so we must use
   *      /action/balance, the same endpoint the startup balance fetch uses.
   *
   * Async because of the getInfo round-trip, so it captures the round token up
   * front and bails if a new round started before the balance arrived — a slow
   * fetch must never settle a finished round into the next round's wallet.
   */
  private async settleFromResult(env: Envelope): Promise<void> {
    const token = this.roundToken;
    const userName = (this.d.api.getCredentials().userName || '').toLowerCase();
    const winLose = extractWinLoseForUser(env?.Data, userName);
    let balance = extractBalanceForUser(env?.Data, userName);

    if (balance == null) {
      // Server didn't include the post-round balance — fetch it from
      // /action/balance.
      balance = await this.fetchLatestBalance();
    }

    if (token !== this.roundToken) {
      debugLog(
        '[Hub] Result settlement dropped — round advanced during balance fetch',
      );
      return;
    }

    this.applyResultSettlement(winLose, balance);
  }

  /**
   * Fetch the latest wallet balance from the backend via /action/balance
   * (checkBalance) — the same endpoint the startup balance fetch uses.
   * (/action/getinfo carries config only, no balance.) Returns null on failure
   * so the caller can fall back to the locally-tracked wallet rather than
   * zeroing the balance on a transient network error.
   */
  private async fetchLatestBalance(): Promise<number | null> {
    try {
      const sessionId = this.d.api.getCredentials().gameSessionId ?? '';
      const balance = await this.d.api.checkBalance({ sessionId });
      debugLog('[Hub] /action/balance post-result balance =', balance);
      return balance;
    } catch (e) {
      warn('[Hub] checkBalance after result failed — keeping local balance:', e);
      return null;
    }
  }

  /**
   * Apply the resolved post-round balance: compute the net P&L from the wallet
   * delta, settle, drive the YOU WIN / YOU LOSE popup, and log the round.
   * `balance` is the authoritative post-round wallet (from the Result event or
   * getInfo); null only when both sources failed, in which case we fall back to
   * `balanceBefore + winLose` (the legacy local computation).
   */
  private applyResultSettlement(winLose: number, balance: number | null): void {
    // Once per round — a retransmitted settlement Result must not re-run this
    // (see didApplyResultSettlement). A second pass would read the already-settled
    // wallet as `balanceBefore`, collapse netWinLose to ~0, and double-log.
    if (this.didApplyResultSettlement) return;
    this.didApplyResultSettlement = true;

    debugLog('[Hub] Result settlement — winLose=', winLose, 'balance=', balance);

    // Capture snapshot before mutating state for the log entry.
    const totalBet = this.d.state.bets.total;
    const betDetails = formatBetDetails(this.d.state.bets);
    const balanceBefore = this.d.state.balance;
    const balanceAfter = balance ?? balanceBefore + winLose;
    // NET round P&L = the change in the player's wallet (post-round balance −
    // pre-round wallet). In MP the wallet (_balance) is never debited per-bet —
    // only settled at round end — so balanceBefore is the pre-bet wallet and the
    // delta is exactly this round's net. Drives the YOU LOSE amount, the game log,
    // and the chip animation. Falls back to winLose only when the server omits Balance.
    const netWinLose = balanceAfter - balanceBefore;
    // WINNINGS (profit, EXCLUDING the staked "modal") from the area the player
    // won — the amount shown on YOU WIN. Per the product rule we show YOU WIN
    // whenever the player won ANY area, even when the round nets a loss: e.g. bet
    // tie 100 + tiger 100, result tiger → won the tiger area → YOU WIN 100 (not a
    // net-0 push / loss). Dragon Tiger resolves to a SINGLE winning area (the
    // round result), so the won stake is the bet on that area and the winnings are
    // stake × payout. Computed locally — independent of the hub's per-user WinLost
    // field, whose net-vs-gross convention is unreliable and can't be decomposed
    // per area from an aggregated row — and captured BEFORE settle() clears the
    // bets below.
    const winningResult = this.d.state.currentRound?.result;
    const wonStake = winningResult ? this.d.state.bets.amountFor(winningResult) : 0;
    const winProfit = winningResult ? wonStake * PAYOUT[winningResult] : 0;

    this.d.state.setMultiplayerWinLose(netWinLose);
    // Defer the balance update to the WIN/LOSE reveal (t=6s), like
    // single-player (req 2). If settlement already happened (a late Result),
    // apply the balance immediately so it isn't lost.
    this.pendingSettleBalance = balanceAfter;
    // If the reveal already reached the settle point and was only waiting on
    // this balance, finalize now (atomic balance update + bet clear). This is
    // the multiplayer path where the Result event lands after the local result
    // sequence — settling atomically here avoids clearing bets early (which
    // would flash the pre-settlement wallet).
    if (this.awaitingSettleBalance && !this.didSettle) {
      this.didSettle = true;
      this.awaitingSettleBalance = false;
      this.d.state.settle(balanceAfter);
    }

    // Apply the win/lose total to the round so the result sequence at
    // t=6s shows the right popup. applyRoundResult no longer touches the
    // history road (that's deferred to the reveal via recordHistoryOnce), so
    // calling it again here for the same round is safe — no duplicate icon.
    const cur = this.d.state.currentRound;
    if (cur) {
      this.d.state.applyRoundResult({
        ...cur,
        totalWinLose: netWinLose,
        positiveWinAmount: winProfit,
        hasPositiveWin: winProfit > 0,
      });

      GameLogManager.add({
        betAmount: totalBet,
        betDetails,
        winLoseAmount: netWinLose,
        balanceBefore,
        balanceAfter,
        gameResult: formatGameResult(cur.result, cur.dragonPoint, cur.tigerPoint),
        roundId: cur.roundId || null,
      });
    }

    // (The visual result sequence was already kicked off in handleResult, before
    // the async balance fetch — see startResultSequence there.)

    // Backstop the YOU WIN / YOU LOSE popup from the AUTHORITATIVE figures
    // computed above — NOT the round captured by startResultSequence, which in
    // multiplayer routinely still holds the StartPlay placeholder because the
    // settlement Result lands after the t=6s reveal already ran. Per the product
    // rule: YOU WIN the won area's stake-free winnings whenever the player won an
    // area (even on a net-loss round), else YOU LOSE the net loss, else nothing.
    // Reveal only once the reveal point has passed (endingRevealDone) or the round
    // was torn down (didResetAfterResult), so it never appears mid-reveal; if the
    // Result arrived BEFORE the reveal, currentRound was patched above and the
    // t=6s reveal shows it instead. Idempotent via didShowWinLose.
    const resultDisplay = winProfit > 0 ? winProfit : netWinLose < 0 ? netWinLose : 0;
    if (this.endingRevealDone || this.didResetAfterResult) {
      this.revealWinLosePanel(resultDisplay);
    }
  }

  /**
   * PlaceBet ack — server confirmed (part of) our batched bet submission.
   *
   * No-op for balance: bets are submitted as a single batch when the
   * countdown ends and the displayed balance is optimistic (wallet −
   * pending bets). The authoritative wallet is re-synced from the Result
   * event at settlement, so applying the ack's per-bet balance here would
   * double-count the deduction.
   */
  private handlePlaceBetAck(_env: Envelope): void {
    // intentionally empty (see above)
  }

  // -----------------------------------------------------------------
  // Multiplayer countdown timers (1-Hz tick)
  // -----------------------------------------------------------------

  /**
   * Drive a 1-Hz countdown for the bet phase. Mirrors
   * `_startBetCountdown` (game_bloc.dart:941-961).
   */
  private startBetCountdown(seconds: number): void {
    this.cancelBetCountdown();
    let remaining = Math.max(0, Math.floor(seconds));
    this.d.state.setMultiplayerBetSeconds(remaining);
    this.betCountdownHandle = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        this.cancelBetCountdown();
        this.d.state.setMultiplayerBetSeconds(null);
        // Countdown ended — submit the batched bets now (req 3: multiplayer
        // submits when the countdown ends, not per-chip).
        this.submitMultiplayerBets();
      } else {
        this.d.state.setMultiplayerBetSeconds(remaining);
      }
    }, 1000);
  }

  private cancelBetCountdown(): void {
    if (this.betCountdownHandle != null) {
      window.clearInterval(this.betCountdownHandle);
      this.betCountdownHandle = null;
    }
  }

  /**
   * Drive a 1-Hz countdown for the restart phase. Mirrors
   * `_startRestartCountdown` (game_bloc.dart:963-982).
   */
  private startRestartCountdown(seconds: number): void {
    this.cancelRestartCountdown();
    let remaining = Math.max(0, Math.floor(seconds));
    this.d.state.setMultiplayerRestartSeconds(remaining);
    this.restartCountdownHandle = window.setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        this.cancelRestartCountdown();
        this.d.state.setMultiplayerRestartSeconds(null);
      } else {
        this.d.state.setMultiplayerRestartSeconds(remaining);
      }
    }, 1000);
  }

  private cancelRestartCountdown(): void {
    if (this.restartCountdownHandle != null) {
      window.clearInterval(this.restartCountdownHandle);
      this.restartCountdownHandle = null;
    }
  }

  private stopMultiplayer(): void {
    this.cancelBetCountdown();
    this.cancelRestartCountdown();
    void this.d.hub.disconnect();
    this.d.state.setMultiplayerActive(false);
  }

  // =================================================================
  // Helpers
  // =================================================================

  /** Subset of placed bets that match the winning side. */
  private placedWinningBets(winner: GameResult): Set<BetType> {
    const out = new Set<BetType>();
    const winningId = BET_ITEM_ID[winner as BetType];
    for (const b of this.d.state.bets.bets) {
      if (BET_ITEM_ID[b] === winningId) out.add(b);
    }
    return out;
  }

  private panelCodeFor(r: GameResult): 3 | 4 | 5 {
    return r === 'dragon' ? 3 : r === 'tiger' ? 4 : 5;
  }

  private scheduleTimer(fn: () => void, ms: number): void {
    const id = window.setTimeout(fn, ms);
    this.sequenceTimers.push(id);
  }

  private cancelTimers(): void {
    for (const id of this.sequenceTimers) window.clearTimeout(id);
    this.sequenceTimers = [];
  }
}

// Keep the cast bridge — `GAME_RESULT_HISTORY_VALUE` is exported but only
// used indirectly via the Rive layer; ref to silence unused-var warnings.
export const _unused = GAME_RESULT_HISTORY_VALUE;

function wait(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

function formatCurrency(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

function ensureUrlEncoded(token: string): string {
  if (!token) return token;
  if (token.includes('%')) return token;
  return encodeURIComponent(token);
}

function parseGameUrl(
  currentGameUrl: string,
  loginToken: string,
): { gameToken: string; gsid: string } {
  if (!currentGameUrl) return { gameToken: loginToken, gsid: '' };
  try {
    const u = new URL(currentGameUrl, 'https://placeholder.invalid');
    const urlToken = u.searchParams.get('token');
    const urlGsid = u.searchParams.get('gsid');
    return {
      gameToken: urlToken && urlToken.length > 0 ? urlToken : loginToken,
      gsid: urlGsid ?? '',
    };
  } catch {
    return { gameToken: loginToken, gsid: '' };
  }
}

/**
 * Format the round result for the bet-history log. Examples:
 *   "Dragon Win (7 vs 3)"  /  "Tiger Win (5 vs J)"  /  "Tie (10 vs 10)"
 */
function formatGameResult(
  winner: GameResult,
  dragonPoint: number,
  tigerPoint: number,
): string {
  const d = pointName(dragonPoint);
  const t = pointName(tigerPoint);
  if (winner === 'tie') return `Tie (${d} vs ${t})`;
  const w = winner === 'dragon' ? 'Dragon' : 'Tiger';
  return `${w} Win (${d} vs ${t})`;
}

function pointName(p: number): string {
  switch (p) {
    case 1:  return 'A';
    case 11: return 'J';
    case 12: return 'Q';
    case 13: return 'K';
    default: return String(p);
  }
}

/** "Dragon:1000, Tie:500" — empty string if no bets placed. */
function formatBetDetails(bets: { bets: readonly BetType[]; amountFor(b: BetType): number }): string {
  const parts: string[] = [];
  const label: Record<BetType, string> = { dragon: 'Dragon', tiger: 'Tiger', tie: 'Tie' };
  for (const b of bets.bets) {
    const amt = bets.amountFor(b);
    if (amt > 0) parts.push(`${label[b]}:${formatCurrency(amt)}`);
  }
  return parts.join(', ');
}

function extractMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  try {
    return JSON.stringify(e);
  } catch {
    return String(e);
  }
}

// =====================================================================
// Multiplayer envelope helpers
// =====================================================================

type Envelope = Record<string, unknown> | null;

function asNumber(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

/** Parse "50,100,200" CSV or an array of numbers into a chip-value list. */
function parseChipsField(raw: unknown): number[] {
  const out: number[] = [];
  if (typeof raw === 'string') {
    for (const piece of raw.split(',')) {
      const n = parseInt(piece.trim(), 10);
      if (Number.isFinite(n) && n > 0) out.push(n);
    }
  } else if (Array.isArray(raw)) {
    for (const v of raw) {
      const n = typeof v === 'number' ? v : parseInt(String(v), 10);
      if (Number.isFinite(n) && n > 0) out.push(n);
    }
  }
  return out;
}

/** Time-delta in seconds from two ISO 8601 strings. */
function secondsBetween(start: unknown, end: unknown): number {
  if (typeof start !== 'string' || typeof end !== 'string') return 0;
  const a = Date.parse(start);
  const b = Date.parse(end);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.round((b - a) / 1000);
}

/**
 * Sum the current user's WinLose values out of the Data array on a
 * Result event. Mirrors game_bloc.dart:781-825:
 *   - Data is usually a List<Map>; each map has UserName + WinLost/WinLose.
 *   - When Data is a single Map, it carries TotalWinLose / WinLose at top level.
 *   - Returns 0 if nothing matches.
 */
/**
 * Sum the current user's WinLose values from a Result envelope's Data
 * array. Same SKIP-empty-username / field-name-priority rules as
 * `extractBalanceForUser` above — broadcast entries without a username
 * are info-only and must not be summed into the player's total.
 */
function extractWinLoseForUser(data: unknown, userNameLower: string): number {
  if (data == null || !userNameLower) return 0;

  if (Array.isArray(data)) {
    let sum = 0;
    for (const raw of data) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;
      const u = pickUserName(item);
      if (!u || u !== userNameLower) continue;
      // Field name varies across server versions: WinLost / WinLose / winLost.
      sum += asNumber(item.WinLost ?? item.WinLose ?? item.winLost ?? item.winLose);
    }
    return sum;
  }

  if (typeof data === 'object') {
    const m = data as Record<string, unknown>;
    // For the single-map shape (rare), fall back to TotalWinLose if no
    // username field is present, but still require user match if it is.
    const u = pickUserName(m);
    if (u && u !== userNameLower) return 0;
    return asNumber(m.TotalWinLose ?? m.WinLose ?? m.winLose ?? m.WinLost);
  }

  return 0;
}

/**
 * Extract the current user's Balance from a Result / PlaceBet envelope's
 * `Data` field. Mirrors `_extractBalanceForUser` (game_bloc.dart:1097-1131).
 *
 * Three rules that MUST be observed (each one corresponds to a wrong-fix
 * bug we hit during the port):
 *
 *   1. The primary field name is `Username` (capital-U, lower-n) — the
 *      lowercase / Pascal-case variants only exist on some events. Probe
 *      `Username` → `username` → `UserName` → `userName`.
 *
 *   2. Entries with empty / missing username MUST be skipped, not
 *      matched. Hub broadcasts include "info-only" entries with no
 *      username carrying placeholder Balance values (often 0 or the
 *      dealer's running totals). Treating those as a match drops the
 *      player's balance to 0 mid-round.
 *
 *   3. If the matched entry has `Balance: null`, return null (don't
 *      update). Some "info-only" events for the current user have a
 *      null Balance — the caller should leave the existing balance
 *      untouched.
 *
 * Both array and single-map data shapes apply the same rules.
 */
function extractBalanceForUser(data: unknown, userNameLower: string): number | null {
  if (data == null || !userNameLower) return null;

  if (Array.isArray(data)) {
    for (const raw of data) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;
      const u = pickUserName(item);
      if (!u || u !== userNameLower) continue; // rule 2
      const b = item.Balance ?? item.balance;
      if (b == null) return null;              // rule 3
      const n = typeof b === 'number' ? b : Number(b);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }

  if (typeof data === 'object') {
    const m = data as Record<string, unknown>;
    const u = pickUserName(m);
    if (!u || u !== userNameLower) return null; // rule 2
    const b = m.Balance ?? m.balance;
    if (b == null) return null;                 // rule 3
    const n = typeof b === 'number' ? b : Number(b);
    return Number.isFinite(n) ? n : null;
  }

  return null;
}

/** Probe `Username` (canonical) → `username` → `UserName` → `userName`. */
function pickUserName(item: Record<string, unknown>): string {
  const raw =
    item.Username ?? item.username ?? item.UserName ?? item.userName;
  return raw == null ? '' : String(raw).toLowerCase();
}
